import { useEffect } from 'react'
import { BrowserRouter } from 'react-router-dom'
import { api } from '@/api'
import { useConsoleChrome } from '@/components/layout'
import { ConsoleRoutes } from '@/routes'
import { watchSession } from '@/store/authStore'

/**
 * The application: a router, a session watcher, and the document chrome.
 *
 * Almost nothing lives here on purpose. The route table is data in `routes.tsx`
 * and the signed-in frame is `components/app/ConsoleFrame`, so this file stays
 * the list of things that must happen exactly once for the whole console and
 * cannot sensibly happen anywhere else.
 *
 * `useConsoleChrome` is called here rather than inside the shell, and the
 * difference is one screen: the sign-in page has no shell around it. Somebody
 * who pinned the console to dark and then signed out would otherwise be handed
 * a cream page on the one screen the product uses to introduce itself. What it
 * writes onto the document element, and why the scheme travels as an attribute
 * rather than through components, is set out in
 * `components/layout/preferences.ts`.
 */
export function App() {
  useConsoleChrome()

  /*
   * The client tells this store when a session ends without anybody asking: a
   * refused refresh, or a 401 that survived a retry. Subscribing here rather
   * than at import time keeps the client free of any knowledge of the store,
   * and returning the unsubscribe means StrictMode's deliberate double mount
   * does not leave two listeners behind, each pushing its own redirect.
   */
  useEffect(() => watchSession(api.client), [])

  return (
    <BrowserRouter>
      <ConsoleRoutes />
    </BrowserRouter>
  )
}
